import { BottomSheet, type UseModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { useCallback, useEffect, useMemo, useState } from "react";

import { SHIFT_DISCOVERY_SHIFT_MODAL_PATH } from "../../../ShiftDiscovery/paths";
import { useShiftModalsDataContext } from "../../../ShiftDiscovery/useShiftModalsDataContext";
import { ShiftBottomSheetCardsWrapper } from "../../BottomSheet/CardsWrapper";
import { ShiftFailedLoadingCard } from "../../BottomSheet/ShiftFailedLoadingCard";
import { computeShiftPay } from "../computeDoubleShiftPays";
import { useCompanionOffers } from "../hooks/useCompanionOffers";
import { ShiftDoubleBookingFooter } from "./BookingFooter";
import { ShiftDoubleBottomSheetContent } from "./Content";
import { ShiftDoubleBottomSheetHeader } from "./Header";

export interface ShiftDoubleBottomSheetProps {
  modalState: UseModalState;
}

export function ShiftDoubleBottomSheet(props: ShiftDoubleBottomSheetProps) {
  const { modalState } = props;

  const { shift, double, shiftFailedLoading, navigateToModal } = useShiftModalsDataContext();

  const { workplace } = shift ?? {};

  const hasRequiredData = isDefined(double) && isDefined(shift) && isDefined(workplace);

  const [selectedCompanionId, setSelectedCompanionId] = useState(
    double?.companionShifts[0]?.id ?? ""
  );

  // Sync selection when double data arrives or changes
  useEffect(() => {
    const firstCompanionId = double?.companionShifts[0]?.id;
    if (isDefined(firstCompanionId)) {
      setSelectedCompanionId(firstCompanionId);
    }
  }, [double?.companionShifts]);

  // Create/refresh offers for companion shifts so we have pay data and offer IDs for booking
  const companionShiftIds = useMemo(
    () => double?.companionShifts.map((c) => c.id) ?? [],
    [double?.companionShifts]
  );
  const { companionOffers, isLoading: isCompanionOffersLoading } =
    useCompanionOffers(companionShiftIds);

  const requiresLunchBreak = workplace?.attributes.requiresLunchBreak ?? false;

  // Enrich companion pay from freshly created offers when context data is missing
  const enrichedCompanionShiftPays = useMemo(() => {
    if (!isDefined(double)) {
      return undefined;
    }

    const paysMap = new Map(double.companionShiftPays);
    for (const companion of double.companionShifts) {
      if (!paysMap.has(companion.id)) {
        const offer = companionOffers.get(companion.id);
        if (isDefined(offer)) {
          paysMap.set(
            companion.id,
            computeShiftPay(
              { start: companion.attributes.start, end: companion.attributes.end },
              offer.hourlyPayInMinorUnits,
              requiresLunchBreak
            )
          );
        }
      }
    }

    return paysMap;
  }, [double, companionOffers, requiresLunchBreak]);

  // Derive the selected companion and its pay
  const selectedCompanion = double?.companionShifts.find((c) => c.id === selectedCompanionId);
  const selectedCompanionPay = selectedCompanion
    ? enrichedCompanionShiftPays?.get(selectedCompanion.id)
    : undefined;

  // Get the selected companion's offer ID (from freshly created offer or existing relationship)
  const selectedCompanionOfferId = selectedCompanion
    ? (companionOffers.get(selectedCompanion.id)?.offerId ??
      selectedCompanion.relationships.offer?.data.id)
    : undefined;

  const handleShiftPress = useCallback(
    (shiftId: string) => {
      navigateToModal(SHIFT_DISCOVERY_SHIFT_MODAL_PATH, {
        pathParams: { shiftId },
      });
    },
    [navigateToModal]
  );

  // Build enriched double with companion pays for Content display
  const enrichedDouble = useMemo(() => {
    if (!isDefined(double)) {
      return undefined;
    }

    return { ...double, companionShiftPays: enrichedCompanionShiftPays };
  }, [double, enrichedCompanionShiftPays]);

  return (
    <BottomSheet modalState={modalState}>
      {hasRequiredData && isDefined(enrichedDouble) && (
        <>
          <ShiftDoubleBottomSheetHeader
            workplaceName={workplace.attributes.name}
            onClose={() => {
              modalState.closeModal();
            }}
          />

          <ShiftDoubleBottomSheetContent
            shift={shift}
            double={enrichedDouble}
            workplaceTimezone={workplace.attributes.location.timezone}
            selectedCompanionId={selectedCompanionId}
            onSelectCompanion={setSelectedCompanionId}
            onShiftPress={handleShiftPress}
          />

          {isDefined(selectedCompanion) && (
            <ShiftDoubleBookingFooter
              shift={shift}
              double={enrichedDouble}
              selectedCompanion={selectedCompanion}
              selectedCompanionPay={selectedCompanionPay}
              selectedCompanionOfferId={selectedCompanionOfferId}
              isCompanionOffersLoading={isCompanionOffersLoading}
              hideTotalPay={double.companionShifts.length > 1}
            />
          )}
        </>
      )}

      {!hasRequiredData && shiftFailedLoading && (
        <ShiftBottomSheetCardsWrapper>
          <ShiftFailedLoadingCard />
        </ShiftBottomSheetCardsWrapper>
      )}
    </BottomSheet>
  );
}
